import { useCallback, useState } from 'react';
import { View } from 'react-native';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { CreateResponsiveStyle, DEVICE_SIZES } from 'rn-responsive-styles';

import { Button, HeaderBackButton, LoadingOverlay, TabsContainer, Text, spacing } from '@pletly/ui';
import { RootStackScreenProps } from '../../../navigation/types';
import { withTranslation } from '../../../locales/hoc';
import { TeamsCard } from '../../components/TeamsCard';
import { TeamCaregiversTable } from './components/TeamCaregiversTable';
import { TeamMemberDataType } from './components/TeamMembersTable';
import { TeamCareRecipientsTable } from './components/TeamCareRecipientsTable';
import { useTeamData } from '../components/useTeamData';
import { OrganisationRole, useOrganisationInfo } from '../../..';

export type OrganisationTeamScreenParams = {
	organisationUuid: string;
	teamUuid: string;
};

type Props = RootStackScreenProps<'OrganisationTeam'>;

const OrganisationTeam = ({
	route: {
		params: { teamUuid: uuid, organisationUuid },
	},
	navigation: { navigate },
}: Props) => {
	const { t } = useTranslation();
	const styles = useStyles();
	const teamData = useTeamData({ teamUuid: uuid, organisationUuid });
	const [tabIndex, setTabIndex] = useState(0);

	const { organisationInfo } = useOrganisationInfo();

	const handleSettingsPress = useCallback(() => {
		navigate('EditTeam', { uuid, organisationUuid });
	}, [uuid, organisationUuid, navigate]);

	const handleCreatePress = useCallback(() => {
		navigate('NewTeam', { organisationUuid, parentTeamUuid: uuid });
	}, [navigate, organisationUuid, uuid]);

	if (teamData.state === 'loading') {
		return <LoadingOverlay show />;
	}

	if (teamData.state === 'error') {
		return <Text>{teamData.errorMessage}</Text>;
	}

	const canModify =
		!!organisationInfo &&
		[OrganisationRole.Admin, OrganisationRole.Manager].includes(organisationInfo.role);
	const { currentTeam, allTeams } = teamData;
	const canCreateSubTeam = !currentTeam.parentTeam || !currentTeam.parentTeam.parentTeam;

	const tabs = getTabs(currentTeam, organisationUuid, t, !canModify);

	return (
		<View>
			<View style={styles.container}>
				<View style={styles.mainContent}>
					<View style={styles.header}>
						<View style={styles.headerTitle}>
							<HeaderBackButton />
							<Text category="h3">{currentTeam.name}</Text>
						</View>
						{canModify && (
							<View style={styles.actionsRow}>
								{canCreateSubTeam && (
									<Button variant="filled" color="primary" onPress={handleCreatePress}>
										{t('teams.new-sub-team')}
									</Button>
								)}
								<Button variant="outline" color="primary" onPress={handleSettingsPress}>
									{t('settings')}
								</Button>
							</View>
						)}
					</View>
					<View>
						<TabsContainer tabs={tabs} currentIndex={tabIndex} onIndexChange={setTabIndex} />
					</View>
				</View>

				{canModify && (
					<TeamsCard
						organisationUuid={organisationUuid}
						teams={allTeams}
						activeTeam={currentTeam}
					/>
				)}
			</View>
		</View>
	);
};

export const OrganisationTeamPage = withTranslation(OrganisationTeam);

const useStyles = CreateResponsiveStyle(
	{
		container: {
			paddingVertical: spacing(4),
			maxWidth: 1180,
			width: '100%',
			flexWrap: 'wrap',
			justifyContent: 'center',
			marginHorizontal: 'auto',
			flexDirection: 'row',
			gap: spacing(4),
			padding: spacing(2),
		},
		mainContent: {
			flex: 1,
			minWidth: 400,
			gap: spacing(3),
		},
		header: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'flex-start',
		},
		headerTitle: {
			gap: spacing(),
		},
		actionsRow: { flexDirection: 'row', gap: spacing(1) },
	},
	{
		[DEVICE_SIZES.LARGE_DEVICE]: {
			container: {
				maxWidth: 1280,
				padding: 0,
			},
		},
	},
);

const getTabs = (
	currentTeam: {
		uuid: string;
		members: {
			caregivers: TeamMemberDataType[];
			careRecipients: TeamMemberDataType[];
			archived: TeamMemberDataType[];
		};
	},
	organisationUuid: string,
	t: TFunction,
	viewOnly = false,
) => {
	const {
		uuid,
		members: { caregivers, careRecipients },
	} = currentTeam;

	return [
		{
			name: 'caregivers',
			label: t('tab-label-caregivers'),
			component: (
				<TeamCaregiversTable
					data={caregivers}
					organisationUuid={organisationUuid}
					teamUuid={uuid}
					viewOnly={viewOnly}
				/>
			),
		},
		{
			name: 'careRecipients',
			label: t('tab-label-care-recipients'),
			component: (
				<TeamCareRecipientsTable
					data={careRecipients}
					organisationUuid={organisationUuid}
					teamUuid={uuid}
					viewOnly={viewOnly}
				/>
			),
		},
	];
};
